<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import bigjs from 'big.js'

import type { TableColumnProps } from '~/components/g/Table.vue'
import { fetchCollectionInfo } from '@/utils/api/dim'
import { formatNumberWithUnit, percentToDecimal } from '@/utils/number'

definePageMeta({ title: '达人', defaultLayoutNoPadding: true })

const { t } = useI18n()
useSeoMeta({
  title: t('blogger.list.pageTitle'),
  keywords: t('blogger.list.pageKeyword'),
  description: t('blogger.list.pageDescription'),
}, { tagPriority: 5 })

const { $request, $getFilterOptions } = useNuxtApp()
const route = useRoute()
const isMounted = useMounted()

const keyword = computed(() => route.query.keyword)
const { isBrHost } = useJudgeBrHost()
const filter = ref<Record<string, any>>({
  regionId: isBrHost.value ? '54' : '1',
})

const isUs = computed(() => {
  return filter.value.regionId === '1'
})

const columns: Ref<TableColumnProps[]> = computed(() => {
  return [
    {
      title: t('达人'),
      dataIndex: 'bloggerData',
      width: 320,
      align: 'left',
      fixed: 'left',
      slotName: 'blogger',
    },
    {
      title: t('粉丝数'),
      dataIndex: 'followerCount',
      width: 90,
      enWidth: 100,
      ptWidth: 130,
      align: 'right',
      slotName: 'number',
      sortable: true,
    },
    {
      title: t('新增粉丝数'),
      slotName: 'number',
      width: 110,
      enWidth: 140,
      ptWidth: 130,
      dataIndex: 'incFollowerCount',
      sortable: true,
      align: 'right',
      tip: t('近30天内新增粉丝数'),
    },
    {
      title: t('预估销量'),
      dataIndex: 'incSoldCount',
      width: 100,
      enWidth: 176,
      ptWidth: 130,
      align: 'right',
      slotName: 'number',
      tip: t('近30天内预估销量'),
      sortable: true,
    },
    {
      title: t('预估销售额'),
      dataIndex: 'incSoldGmv',
      width: 120,
      enWidth: 170,
      ptWidth: 130,
      align: 'right',
      slotName: 'number',
      showUnit: true,
      isPrice: true,
      tip: t('近30天内预估销售额'),
      sortable: 'descend',
    },
    isUs.value && {
      title: t('履约率'),
      dataIndex: 'fulfillmentRate',
      width: 100,
      enWidth: 210,
      align: 'right',
      slotName: 'number',
      sortable: true,
      tip: t('达人收到样品后发布带货视频或直播的预计频率，用于评估其履行推广承诺的可靠性'),
      isRate: true,
    },
    {
      title: t('新增视频'),
      dataIndex: 'incVideoCount',
      width: 100,
      enWidth: 120,
      ptWidth: 130,
      align: 'right',
      slotName: 'number',
      tip: t('近30天内新增视频'),
      sortable: true,
    },
    {
      title: t('直播场次'),
      dataIndex: 'incLiveCount',
      width: 90,
      ptWidth: 130,
      enWidth: 100,
      align: 'right',
      slotName: 'number',
      sortable: true,
    },
    {
      title: t('平均播放量'),
      align: 'right',
      width: 110,
      enWidth: 120,
      ptWidth: 140,
      slotName: 'number',
      dataIndex: 'incVideoAvgPlayCount',
      tip: t('近30天内视频的平均播放量'),
      sortable: true,
    },
    {
      title: t('场均观看人次'),
      align: 'right',
      width: 120,
      enWidth: 140,
      ptWidth: 140,
      slotName: 'number',
      dataIndex: 'incLiveAvgBloggerCount',
      tip: t('近30天内直播间场均观看人次'),
      sortable: true,
    },
    {
      title: t('操作'),
      align: 'center',
      slotName: 'operation',
      dataIndex: 'operation',
      fixed: 'right',
      width: 84,
    },
  ].filter(Boolean) as TableColumnProps[]
})

const { pagination, sort, onSortChange } = useTable(
  { sort: { order: 2, orderName: 'incSoldGmv' } },
)
const filterParams = computed(() => {
  const filterData = {
    ...filter.value,
    keyword: keyword.value,
    isShowBlogger: filter.value.isShowBlogger ? '1' : '',
    minIncVideoSoldGmv: filter.value.minIncVideoSoldGmv ? bigjs(filter.value.minIncVideoSoldGmv).mul(100).toNumber() : filter.value.minIncVideoSoldGmv,
    maxIncVideoSoldGmv: filter.value.maxIncVideoSoldGmv ? bigjs(filter.value.maxIncVideoSoldGmv).mul(100).toNumber() : filter.value.maxIncVideoSoldGmv,
    minIncLiveAvgSoldGmv: filter.value.minIncLiveAvgSoldGmv ? bigjs(filter.value.minIncLiveAvgSoldGmv).mul(100).toNumber() : filter.value.minIncLiveAvgSoldGmv,
    maxIncLiveAvgSoldGmv: filter.value.maxIncLiveAvgSoldGmv ? bigjs(filter.value.maxIncLiveAvgSoldGmv).mul(100).toNumber() : filter.value.maxIncLiveAvgSoldGmv,
    minFulfillmentRate: filter.value.minFulfillmentRate ? percentToDecimal(`${filter.value.minFulfillmentRate}%`) : filter.value.minFulfillmentRate,
    maxFulfillmentRate: filter.value.maxFulfillmentRate ? percentToDecimal(`${filter.value.maxFulfillmentRate}%`) : filter.value.maxFulfillmentRate,
    productCategoryIds: filter.value?.productCid?.split(',')?.pop(),
  }

  return {
    ...sort.value,
    ...filterData,
  }
})
const { toggleTableMask, tableMask } = useExpired()
let cachedData: Api.TableResponse<any> | null = null
const { data, isFetching } = useQuery({
  queryKey: ['/analysis/bloggers', filterParams, pagination],
  queryFn: async ({ signal }) => {
    try {
      toggleTableMask()
      const res = await $request<Api.TableResponse>('/analysis/bloggers', {
        query: {
          ...pagination.value,
          ...filterParams.value,
        },
        signal,
      })
      const collection = await fetchCollectionInfo(res.data.map(item => item.bloggerId).join(','), 4)
      res.data.forEach((item) => {
        item.bloggerData = {
          bloggerId: item.bloggerId,
          uniqueId: item.uniqueId,
          nickName: item.nickName,
          avatar: item.avatar,
          bloggerCategoryNameCn: item.bloggerCategoryCn,
          bloggerCategoryNameEn: item.bloggerCategoryEn,
          bloggerCategoryNamePt: item.bloggerCategoryPt,
          regionId: item.regionId,
          isShopAccount: item.isShopAccount,
        }
        item.isFavorite = collection[item.bloggerId]?.isFavorite
      })
      cachedData = res
      return res
    }
    catch (error: any) {
      toggleTableMask(error?.code)
      return tableMask.value ? cachedData : null
    }
  },
})

const tempFilter = ref<{ [x: string]: any }>({})

function onDropdownOk() {
  filter.value = { ...filter.value, ...tempFilter.value }
}
function onDropdownReset(delKeys: string[]) {
  for (const key of delKeys) {
    tempFilter.value[key] = ''
  }
  onDropdownOk()
}

const categoryNameKey = useI18nKeys('categoryName')

const options: Record<string, any[]> = {
  FollowerCount: $getFilterOptions().fansCount,
  contactType: [
    { label: 'Ins', value: '1' },
    { label: 'Twitter', value: '2' },
    { label: 'Youtube', value: '3' },
    { label: 'Bio_Url', value: '4' },
    { label: t('邮箱'), value: '5' },
  ],
  portraitAge: $getFilterOptions().ages,
  IncVideoAvgPlayCount: [
    { label: t('以上', [formatNumberWithUnit(1000)]), minValue: 1000 },
    { label: t('以上', [formatNumberWithUnit(5000)]), minValue: 5000 },
    { label: t('以上', [formatNumberWithUnit(10000)]), minValue: 10000 },
    { label: t('以上', [formatNumberWithUnit(100000)]), minValue: 100000 },
  ],
}

const curRegion = ref<Api.User.Region>()
const selected = ref({
  productCid: [] as Api.Dim.Product.Category[],
  bloggerCid: [] as Option[],
})

function handleMinMaxShow(label: string, param: { min?: string, max?: string, prefix?: string, suffix?: string, key: string, isPrice?: boolean }) {
  const { prefix = '', suffix = '', key, isPrice } = param
  let { min, max } = param
  if (min) {
    min = formatNumberWithLocale(min)
  }
  if (max) {
    max = formatNumberWithLocale(max)
  }
  const opts = options[key]
  const isPrefixAppend = isPrice && filter.value.regionId === '2'
  if (opts) {
    const opt = opts.find(item => item.maxValue === max && item.minValue === min)
    if (opt) {
      return { label: `${t(label)}: ${opt.label}`, value: key }
    }
  }

  return min || max
    ? {
        label: `${t(label)}: ${min && max ? `${isPrefixAppend ? '' : prefix}${min}${isPrefixAppend ? prefix : ''}${suffix}-${isPrefixAppend ? '' : prefix}${max}${isPrefixAppend ? prefix : ''}${suffix}` : t(min ? '以上' : '以下', [`${isPrefixAppend ? '' : prefix}${min || max}${isPrefixAppend ? prefix : ''}${suffix}`])}`,
        value: key,
      }
    : null
}
function handleRadioShow(label: string, param: { value: string, key: string }) {
  const { key, value } = param
  return value ? { label: `${t(label)}: ${options[key].find(item => item.value === value)?.label}`, value: key } : null
}

const selectedOptions = computed(() => {
  const { bloggerCid, productCid } = selected.value
  return [
    ...(productCid.length
      ? [{
          kind: 'productCid',
          // label: `${productCid[0]?.[categoryNameKey.value]}`,
          label: productCid.length > 1 ? `${productCid[0][categoryNameKey.value]}: ${productCid[productCid.length - 1][categoryNameKey.value]}` : `${productCid[0][categoryNameKey.value]}`,
          value: filter.value.productCid,
        }]
      : []),
    ...bloggerCid.map(item => ({ ...item, kind: 'bloggerCid' })),
    handleMinMaxShow('粉丝数', { min: filter.value.minFollowerCount, max: filter.value.maxFollowerCount, key: 'FollowerCount' }),
    handleMinMaxShow('直播场次', { min: filter.value.minIncLiveCount, max: filter.value.maxIncLiveCount, key: 'IncLiveCount' }),
    handleMinMaxShow('blogger.场均观看人次', { min: filter.value.minIncLiveAvgBloggerCount, max: filter.value.maxIncLiveAvgBloggerCount, key: 'IncLiveAvgBloggerCount' }),
    handleMinMaxShow('场均销售额', { min: filter.value.minIncLiveAvgSoldGmv, max: filter.value.maxIncLiveAvgSoldGmv, prefix: curRegion.value ? curRegion.value.currencySymbol : '$', key: 'IncLiveAvgSoldGmv', isPrice: true }),
    handleMinMaxShow('带货视频', { min: filter.value.minIncSoldVideoCount, max: filter.value.maxIncSoldVideoCount, key: 'IncSoldVideoCount' }),
    handleMinMaxShow('平均播放量', { min: filter.value.minIncVideoAvgPlayCount, max: filter.value.maxIncVideoAvgPlayCount, key: 'IncVideoAvgPlayCount' }),
    handleMinMaxShow('履约率', { min: filter.value.minFulfillmentRate, max: filter.value.maxFulfillmentRate, key: 'FulfillmentRate', suffix: '%' }),
    handleMinMaxShow('视频销售额', { min: filter.value.minIncVideoSoldGmv, max: filter.value.maxIncVideoSoldGmv, prefix: curRegion.value ? curRegion.value.currencySymbol : '$', key: 'IncVideoSoldGmv', isPrice: true }),
    filter.value.verifyType ? { label: `${t('认证类型')}: ${t(filter.value.verifyType === '1' ? '蓝V认证' : '个人认证')}`, value: 'verifyType' } : null,
    handleRadioShow('联系方式', { value: filter.value.contactType, key: 'contactType' }),
    filter.value.portraitGender ? { label: `${t('性别')}: ${t(filter.value.portraitGender === '1' ? '男性居多' : '女性居多')}`, value: 'portraitGender' } : null,
    handleRadioShow('年龄', { value: filter.value.portraitAge, key: 'portraitAge' }),
    filter.value.isShopAccount ? { label: `${t('账号类型')}: ${t(filter.value.isShopAccount === '1' ? '店铺账号' : '达人账号')}`, value: 'isShopAccount' } : null,
    filter.value.mcn ? { label: `${t('签约机构')}: ${t(filter.value.mcn === '1' ? '已签约' : '未签约')}`, value: 'mcn' } : null,
    filter.value.isShowBlogger ? { label: `${t('只看带货达人')}`, value: 'isShowBlogger' } : null,
  ].filter(Boolean) as Option[]
})
function onClearSelected(options: Option[]) {
  options.forEach((opt) => {
    if (opt.kind === 'productCid') {
      selected.value.productCid = []
      filter.value.productCid = ''
    }
    else if (opt.kind === 'bloggerCid') {
      selected.value.bloggerCid = []
      filter.value.bloggerCid = ''
    }
    // value为大写开头即为minXXX-maxXXX
    else if (/^[A-Z]/.test(opt.value)) {
      delete filter.value[`min${opt.value}`]
      delete filter.value[`max${opt.value}`]
    }
    else {
      delete filter.value[opt.value]
    }
  })
}
watch(filterParams, () => {
  pagination.value.index = 1
})

watch(() => filter.value.regionId, () => {
  if (filter.value.regionId !== '1') {
    filter.value.minFulfillmentRate = undefined
    filter.value.maxFulfillmentRate = undefined
  }
})
</script>

<template>
  <div>
    <a-form
      :model="filter" size="small"
      class="box-border gap-16 g-card-half"
      label-align="left"
      :auto-label-width="isMounted"
      :label-col-props="{ flex: 'none' }" :wrapper-col-props="{ flex: '1' }"
    >
      <FilterRegionRadio v-model="filter.regionId" @change="(_, region) => curRegion = region" />
      <FilterProductRadio v-model="filter.productCid" label="带货分类" @change="(_, opts) => selected.productCid = opts" />
      <FilterBloggerRadio v-model="filter.bloggerCid" @change="(_, opt) => selected.bloggerCid = opt ? [opt] : []" />
      <g-form-item :label="$t('核心标签')" content-class="gap-12 ">
        <div ml-12 w-full flex flex-wrap items-center gap-16>
          <FilterFansCount
            v-model:minValue="filter.minFollowerCount"
            v-model:maxValue="filter.maxFollowerCount"
          />
          <g-dropdown-panel
            :label="$t('直播表现')"
            @reset="onDropdownReset(['minLiveCount', 'maxLiveCount', 'minLiveUsersAvg', 'maxLiveUsersAvg', 'minLiveGmvAvg', 'maxLiveGmvAvg'])"
            @ok="onDropdownOk"
          >
            <template #content>
              <a-form :model="filter" auto-label-width label-align="left">
                <g-form-item class="h-48 items-center">
                  <template #label>
                    <span class="text-[--color-text-3]">{{ $t('直播场次') }}</span>
                  </template>
                  <g-dropdown-number
                    v-model:minValue="tempFilter.minIncLiveCount"
                    v-model:maxValue="tempFilter.maxIncLiveCount"
                    displayType="select"
                    :precision="0"
                    :options="[
                      { label: $t('以上', [1]), minValue: 1 },
                      { label: $t('以上', [5]), minValue: 5 },
                      { label: $t('以上', [10]), minValue: 10 },
                      { label: $t('以上', [20]), minValue: 20 },
                    ]"
                  />
                </g-form-item>

                <g-form-item class="h-48 items-center">
                  <template #label>
                    <span class="text-[--color-text-3]">{{ $t('场均观看人次') }}</span>
                  </template>
                  <g-dropdown-number
                    v-model:minValue="tempFilter.minIncLiveAvgBloggerCount"
                    v-model:maxValue="tempFilter.maxIncLiveAvgBloggerCount"
                    displayType="select"
                    :precision="0"
                    :options="[
                      { label: $t('以上', [100]), minValue: 100 },
                      { label: $t('以上', [500]), minValue: 500 },
                      { label: $t('以上', [formatNumberWithUnit(1000)]), minValue: 1000 },
                      { label: $t('以上', [formatNumberWithUnit(5000)]), minValue: 5000 },
                      { label: $t('以上', [formatNumberWithUnit(10000)]), minValue: 10000 },
                      { label: $t('以上', [formatNumberWithUnit(20000)]), minValue: 20000 },
                    ]"
                  />
                </g-form-item>
                <g-form-item class="h-48 items-center">
                  <template #label>
                    <span class="text-[--color-text-3]">{{ $t('场均销售额') }}</span>
                  </template>
                  <g-input-number
                    v-model:min-value="tempFilter.minIncLiveAvgSoldGmv"
                    v-model:max-value="tempFilter.maxIncLiveAvgSoldGmv"
                    :unit="curRegion ? curRegion.currencySymbol : '$'"
                    size="small"
                  />
                </g-form-item>
              </a-form>
            </template>
          </g-dropdown-panel>
          <g-dropdown-number
            v-model:minValue="filter.minIncSoldVideoCount"
            v-model:maxValue="filter.maxIncSoldVideoCount"
            :label="$t('带货视频')"
            :precision="0"
            :options="[
              { label: $t('以上', [1]), minValue: 1 },
              { label: $t('以上', [5]), minValue: 5 },
              { label: $t('以上', [10]), minValue: 10 },
              { label: $t('以上', [20]), minValue: 20 },
            ]"
          />
          <g-dropdown-number
            v-model:minValue="filter.minIncVideoAvgPlayCount"
            v-model:maxValue="filter.maxIncVideoAvgPlayCount"
            :label="$t('平均播放量')"
            :precision="0"
            :options="options.IncVideoAvgPlayCount"
          />
          <g-dropdown-number
            v-if="isUs"
            v-model:minValue="filter.minFulfillmentRate"
            v-model:maxValue="filter.maxFulfillmentRate"
            :label="$t('履约率')"
            :max="100"
            :options="[
              { label: $t('以上', ['50%']), minValue: 50 },
              { label: $t('以上', ['80%']), minValue: 80 },
              { label: $t('以上', ['90%']), minValue: 90 },
            ]"
          />
          <div class="flex items-center gap-16">
            <span class="ml-12 flex-shrink-0 text-[--color-text-2]">{{ $t('视频销售额') }}</span>
            <g-input-number
              v-model:min-value="filter.minIncVideoSoldGmv"
              v-model:max-value="filter.maxIncVideoSoldGmv"
              :unit="curRegion ? curRegion.currencySymbol : '$'"
            />
          </div>
        </div>
      </g-form-item>
      <g-form-item :label="$t('达人信息')" content-class="gap-12 ">
        <div ml-12 w-full flex flex-wrap items-center gap-16>
          <g-dropdown-radio
            v-model="filter.verifyType"
            :label="$t('认证类型')"
            :options="[
              { label: $t('蓝V认证'), value: '1' },
              { label: $t('个人认证'), value: '0' },
            ]"
          />
          <g-dropdown-radio
            v-model="filter.contactType"
            :label="$t('联系方式')"
            :options="options.contactType"
          />
          <FilterFansPortrait
            v-model:age="filter.portraitAge"
            v-model:gender="filter.portraitGender"
            @reset="onDropdownReset(['portraitGender', 'portraitAge'])"
          />
          <g-dropdown-radio
            v-model="filter.isShopAccount"
            :label="$t('账号类型')"
            :options="[
              { label: t('达人账号'), value: '0' },
              { label: t('店铺账号'), value: '1' },
            ]"
          />
          <g-dropdown-radio
            v-model="filter.mcn"
            :label="$t('签约机构')"
            :options="[
              { label: t('已签约'), value: '1' },
              { label: t('未签约'), value: '0' },
            ]"
          />
          <a-checkbox v-model="filter.isShowBlogger">
            {{ $t('只看带货达人') }}
          </a-checkbox>
        </div>
      </g-form-item>
      <FilterSelected v-if="selectedOptions.length > 0" v-model="selectedOptions" @clear="onClearSelected" />
    </a-form>
    <div class="p-20">
      <g-card class="flex flex-1 flex-col pt-20">
        <template #head>
          <div w-full flex items-center justify-between>
            {{ $t('达人列表') }}
            <a-space :size="16">
              <span class="text-12 text-[--color-text-4]">{{ $t('列表默认显示达人30天数据') }}</span>
              <btns-export :params="filterParams" :down-type="12" />
              <btns-export :params="filterParams" :down-type="13" :title="$t('达人联系方式')" :expiredType="4" />
            </a-space>
          </div>
        </template>
        <g-table
          class="g-hide-last-pagination flex-1"
          row-key="bloggerId"
          :sticky-header="-1"
          :loading="isFetching"
          :columns="columns" :data="data?.data ?? []"
          :scroll="{ x: '100%' }"
          showUserStatus
          :showMask="tableMask"
          :pagination="{ total: data?.pagination.total, current: pagination.index, pageSize: pagination.limit }"
          @page-change="pagination.index = $event"
          @page-size-change="pagination.limit = $event"
          @sorter-change="onSortChange"
        >
          <template #operation="{ record }">
            <a-space class="gap-4">
              <btns-collect :id="record.bloggerData.bloggerId" :status="record.isFavorite" :type="4" />
            </a-space>
          </template>
        </g-table>
      </g-card>
    </div>
  </div>
</template>

<style scoped lang="less"></style>
